/**
 * Predictive Models → Propensity (list)
 *
 * Lists every propensity config for the tenant (any status), grouped by
 * approved / pending / archived. Admins can create new configs from a
 * use-case template, archive existing ones, or click through to the
 * detail editor.
 *
 * Visibility
 * ----------
 * The route is registered unconditionally, but the sidebar entry that
 * lands here is hidden via `useFeature('propensity_scoring')`. Visiting
 * the URL directly when the flag is off renders the EmptyFeature notice.
 *
 * What we deliberately don't do here
 * ----------------------------------
 * - No inline editing. The config detail (mart preview, segments,
 *   validate, approve) is its own page so the list stays scannable
 *   even with a dozen configs.
 * - No "delete". Configs can only be archived; archive preserves the
 *   audit history of who approved what.
 */
import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRounded from '@mui/icons-material/AddRounded';
import ArchiveRounded from '@mui/icons-material/ArchiveRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import { useNavigate } from 'react-router-dom';
import { SnackbarContext } from '../../../ui/SnackbarProvider';
import { useFeature } from '../../../ui/FeaturesProvider';
import { getTenantIdFromStorage } from '../../../services/tenantSettingsService';
import {
  listConfigs,
  listUseCases,
  createConfig,
  archiveConfig,
  ConfigStatus,
  ConfigSummary,
  UseCaseRead,
  ValidationStatus,
} from '../../../services/propensityService';
import SettingsPageHeader from '../../settings/components/SettingsPageHeader';
import { useCan } from '../../../ui/PermissionsProvider';


const STATUS_LABEL: Record<ConfigStatus, string> = {
  draft: 'Draft',
  validated: 'Validated',
  approved: 'Approved',
  archived: 'Archived',
};

const STATUS_COLOR: Record<ConfigStatus, 'default' | 'info' | 'success' | 'warning'> = {
  draft: 'default',
  validated: 'info',
  approved: 'success',
  archived: 'warning',
};

const VALIDATION_COLOR: Record<ValidationStatus, 'success' | 'warning' | 'error'> = {
  green: 'success',
  warn: 'warning',
  refuse: 'error',
};

interface ConfigCardProps {
  cfg: ConfigSummary;
  canEdit: boolean;
  onArchive: (cfg: ConfigSummary) => void;
  onOpen: (cfg: ConfigSummary) => void;
}

function ConfigCard({ cfg, canEdit, onArchive, onOpen }: ConfigCardProps) {
  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      <CardActionArea onClick={() => onOpen(cfg)} sx={{ p: 0 }}>
        <CardContent>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {cfg.display_name}
                </Typography>
                <Chip
                  size="small"
                  label={STATUS_LABEL[cfg.status]}
                  color={STATUS_COLOR[cfg.status]}
                  variant={cfg.status === 'approved' ? 'filled' : 'outlined'}
                  sx={{ height: 22 }}
                />
                {cfg.validation_status && (
                  <Chip
                    size="small"
                    label={`AUC ${cfg.validation_status}`}
                    color={VALIDATION_COLOR[cfg.validation_status]}
                    variant="outlined"
                    sx={{ height: 22 }}
                  />
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {cfg.use_case_key} · model: {cfg.selected_model_key || '—'} · updated{' '}
                {new Date(cfg.updated_at).toLocaleString()}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title="Open">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onOpen(cfg); }}>
                  <OpenInNewRounded fontSize="small" />
                </IconButton>
              </Tooltip>
              {canEdit && cfg.status !== 'archived' && (
                <Tooltip title="Archive">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onArchive(cfg);
                    }}
                  >
                    <ArchiveRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export default function PropensityListPage() {
  const navigate = useNavigate();
  const propensityFeature = useFeature('propensity_scoring');
  const tenantId = React.useMemo(() => getTenantIdFromStorage(), []);
  const canEdit = useCan('manage_predictive_models');
  const { notify } = React.useContext(SnackbarContext);

  const [configs, setConfigs] = React.useState<ConfigSummary[]>([]);
  const [useCases, setUseCases] = React.useState<UseCaseRead[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [createBusy, setCreateBusy] = React.useState(false);
  const [chosenUseCase, setChosenUseCase] = React.useState<string>('');
  const [displayName, setDisplayName] = React.useState<string>('');

  const refresh = React.useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const [cfgs, ucs] = await Promise.all([
        listConfigs(tenantId, { includeArchived: true }),
        listUseCases(tenantId),
      ]);
      setConfigs(cfgs);
      setUseCases(ucs);
    } catch (e: any) {
      notify(e?.message || 'Failed to load propensity configs', 'error');
    } finally {
      setLoading(false);
    }
  }, [tenantId, notify]);

  React.useEffect(() => {
    if (propensityFeature?.enabled) {
      void refresh();
    } else {
      setLoading(false);
    }
  }, [propensityFeature?.enabled, refresh]);

  const handleOpen = React.useCallback(
    (cfg: ConfigSummary) => navigate(`/predictive-models/propensity/${cfg.config_id}`),
    [navigate],
  );

  const handleArchive = React.useCallback(
    async (cfg: ConfigSummary) => {
      if (!tenantId) return;
      try {
        await archiveConfig(tenantId, cfg.config_id);
        notify(`Archived ${cfg.display_name}`, 'success');
        void refresh();
      } catch (e: any) {
        notify(e?.message || 'Archive failed', 'error');
      }
    },
    [tenantId, notify, refresh],
  );

  const handleCreate = React.useCallback(async () => {
    if (!tenantId || !chosenUseCase) return;
    setCreateBusy(true);
    try {
      const cfg = await createConfig(tenantId, {
        use_case_key: chosenUseCase,
        display_name: displayName || undefined,
      });
      notify('Predictive model config created', 'success');
      setCreateOpen(false);
      setChosenUseCase('');
      setDisplayName('');
      navigate(`/predictive-models/propensity/${cfg.config_id}`);
    } catch (e: any) {
      notify(e?.message || 'Create failed', 'error');
    } finally {
      setCreateBusy(false);
    }
  }, [tenantId, chosenUseCase, displayName, notify, navigate]);

  if (!propensityFeature?.enabled) {
    return (
      <Box>
        <SettingsPageHeader description="Build, validate, and deploy propensity models against your training mart." />
        <Alert severity="info">
          Propensity Scoring is not enabled for this workspace. Enable it under{' '}
          <strong>Settings → Features</strong> to start configuring predictive
          models.
        </Alert>
      </Box>
    );
  }

  // Show the empty-state CTA prominently when there are no configs yet —
  // the typical first-time admin lands here from "I just turned on the
  // feature, where do I go?".
  const active = configs.filter((c) => c.status !== 'archived');
  const archived = configs.filter((c) => c.status === 'archived');

  return (
    <Box>
      <SettingsPageHeader description="Build, validate, and deploy propensity models. Each model is configured from a use-case template (insurance lapse, SaaS churn, etc.) and trains on data in your Klaris-managed Redshift mart." />

      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddRounded />}
          disabled={!canEdit || useCases.length === 0}
          onClick={() => setCreateOpen(true)}
        >
          New propensity model
        </Button>
      </Stack>

      {!canEdit && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You're viewing this page in read-only mode. Admin role required to
          create, edit, or approve models.
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!loading && active.length === 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              No models configured yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Pick a use-case template to get started. Klaris provisions a
              dedicated training mart in Redshift; once you load data into
              that mart, run validation and approve the model for use in chat.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={() => setCreateOpen(true)}
              disabled={!canEdit}
            >
              Configure your first model
            </Button>
          </CardContent>
        </Card>
      )}

      {!loading && active.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Active
          </Typography>
          {active.map((cfg) => (
            <ConfigCard
              key={cfg.config_id}
              cfg={cfg}
              canEdit={canEdit}
              onOpen={handleOpen}
              onArchive={handleArchive}
            />
          ))}
        </Box>
      )}

      {!loading && archived.length > 0 && (
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Archived
          </Typography>
          {archived.map((cfg) => (
            <ConfigCard
              key={cfg.config_id}
              cfg={cfg}
              canEdit={canEdit}
              onOpen={handleOpen}
              onArchive={handleArchive}
            />
          ))}
        </Box>
      )}

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New propensity model</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Choose the use case that best matches the outcome you want to
            predict. You can rename the model after creation.
          </DialogContentText>
          <Stack spacing={2}>
            <Select
              fullWidth
              size="small"
              value={chosenUseCase}
              onChange={(e) => setChosenUseCase(String(e.target.value))}
              displayEmpty
            >
              <MenuItem value="" disabled>
                Select a use case…
              </MenuItem>
              {useCases.map((uc) => (
                <MenuItem key={uc.key} value={uc.key}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>{uc.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {uc.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            <TextField
              fullWidth
              size="small"
              label="Display name (optional)"
              placeholder="e.g. Q3 Lapse Model"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!chosenUseCase || createBusy}
            onClick={handleCreate}
          >
            {createBusy ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
